import React, { useState, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { PreloaderWrapper } from 'components/containers'
import Alert from 'components/ui/Alert/Alert'
import { hippaSelector } from 'store/reducers/hippa.reducer'
import image9 from 'assets/images/image9.svg'
import image10 from 'assets/images/image10.svg'
import './upload_sign_form.scss'
import pdfIcon from 'assets/images/pdf_icon.svg';
import deleteIcon from 'assets/images/deletebutton.png';
import visibility_icon from 'assets/images/visibility.png';
import upload_sucess from 'assets/images/request_guidance.gif';
import { api2 } from 'store/apis';
import { useDispatch } from 'react-redux';
import rectangleBg from 'assets/images/background.svg';
import link_expire from 'assets/images/link_expire.gif';
import ExpiredLink from 'components/containers/ExpiredLink';
import { useHippaLinkValidation } from 'hooks/useHippaLinkValidation';

type GovIdFiles = {
    front: File | string  | null
    back: File | string  | null
}

export const UploadSignForm = () => {
    const dispatch = useDispatch();
    const { isSubmitting, success } = useSelector(hippaSelector)
    const [authSignFile, setAuthSignFile] = useState<File | string | null>(null);
    const [fileError, setFileError] = useState<string | null>(null);
    const [isDocumentUploaded, setIsDocumentUploaded] = useState(false);
    const [lawFirmName, setLawFirmName] = useState<string>('');
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [triedAuthUpload, setTriedAuthUpload] = useState(false);
    const [providerData, setProviderData] = useState<any>(null);
    const [govtIdFilesError, setGovtIdFilesError] = useState<GovIdFiles>({front: null, back: null,});
    const [govtIdFiles, setGovtIdFiles] = useState<GovIdFiles>({front: null, back: null,});
    const [isLoading, setIsLoading] = useState(false);


    const auth_document_id = useMemo(() => {
      const params = new URLSearchParams(window.location.search);
      return params.get("auth_document");
    }, []);
    const government_id = useMemo(() => {
      const params = new URLSearchParams(window.location.search);
      return params.get("government_id");
    }, []);

    useEffect(() => {
      if (!auth_document_id) return;
      let isMounted = true; 
      const fetchData = async () => {
        try {
          const response = await api2.get(`/api/v2/provider_landing/get_auth_document?id=${auth_document_id}`)(dispatch, false, true);
          const responseData = response.data ?? response;
          if (isMounted) {
            setProviderData(responseData);
            if (responseData?.verification_checklist?.govt_front_verified) {
              setGovtIdFiles(prev => ({...prev, front: responseData?.signed_documents?.govt_id_front}));
            }
            if (responseData?.verification_checklist?.govt_back_verified) {
              setGovtIdFiles(prev => ({...prev, back: responseData?.signed_documents?.govt_id_back}));
            }
            if (responseData?.verification_checklist?.hippa_verified) {
              setAuthSignFile(responseData?.signed_documents?.signed_authorization_url);
            }
          }
        } catch (err) {
          console.error(err);
        }
      };
      fetchData();
      console.log("Fetching provider data for auth_document_id:", providerData);

      return () => {
        isMounted = false;
      };
    }, []);

    const { isCheckingLink, isLinkExpired } = useHippaLinkValidation();

    const handleFileChange = (
        e: React.ChangeEvent<HTMLInputElement>,
        type: keyof GovIdFiles
    ) => {
        const file = e.target.files?.[0]
        if (!file) return;

        if (file.type !== 'image/jpeg' && file.type !== 'image/png') {
          setGovtIdFilesError(prev => ({ ...prev, [type]: 'Please upload a valid Govt ID JPG / PNG format' }));
          e.target.value = '';
          return;
        }
        if (file.size > 20 * 1024 * 1024) {
          setGovtIdFilesError(prev => ({ ...prev, [type]: 'Please upload a file less than 20Mb' }));
          setGovtIdFiles(prev => ({ ...prev, [type]: null }));
          e.target.value = '';
          return;
        }

        setGovtIdFiles(prev => ({ ...prev, [type]: file }))
        setGovtIdFilesError(prev => ({ ...prev, [type]: null }));
    }

    const removeFile = (type: keyof GovIdFiles) => {
        setGovtIdFiles(prev => ({ ...prev, [type]: null }))
    }

    const handleFileChangeForAuthorization = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];

      if (!file) return;
      if (file.type !== 'application/pdf') {
        setFileError('Please upload a valid PDF document');
        setTriedAuthUpload(true);
        setAuthSignFile(null);
        e.target.value = '';
        return;
      }
      if (file.size > 20 * 1024 * 1024) {
        setFileError('Please upload a file less than 20Mb');
        setTriedAuthUpload(true);
        setAuthSignFile(null);
        e.target.value = '';
        return;
      }

      setFileError(null);
      setAuthSignFile(file);
    };

    const removeFileAuthorization = () => {
      setAuthSignFile(null);
    };


    const handleSubmit = async () => {

      const authDocumentId = new URLSearchParams(window.location.search).get('auth_document') || undefined;

      setSubmitAttempted(true);

      if (!authSignFile) {
        return;
      }

      if (government_id === "Required" && (!govtIdFiles.front || !govtIdFiles.back)) {
        return;
      }

      try {
        setIsLoading(true);
        const formData = new FormData();
        if (govtIdFiles.front) formData.append('govt_id_front', govtIdFiles.front);
        if (govtIdFiles.back) formData.append('govt_id_back', govtIdFiles.back);
        if (authSignFile) formData.append('signed_authorization', authSignFile);
        formData.append('id', auth_document_id || '');
        const response = await api2.post('/api/v2/provider_landing/upload_hippa_documents', formData)(dispatch, false, true);
        setIsDocumentUploaded(true);
        setLawFirmName(response.data.name);
        console.log('document uploaded successfully:', response);
      } catch (error) {
        setIsDocumentUploaded(false);
        console.error('document upload error:', error);
      } finally {
        setIsLoading(false);
      }
    }

    const hasGovtIdError = (type: keyof GovIdFiles) => {
      if (government_id !== 'Required') return false;
      if (govtIdFilesError[type] && !govtIdFiles[type]) return true;
      if (submitAttempted && !govtIdFiles[type]) return true;
      return false;
    };

    const renderUploadBox = (
        label: string,
        file: File | string | null,
        type: keyof GovIdFiles
    ) => {
        const isError = hasGovtIdError(type);
        const isVerified = (typeof file === 'string') ? type === 'front' ? providerData?.verification_checklist?.govt_front_verified : providerData?.verification_checklist?.govt_back_verified : false;
        return (
            <div className={`upload-box-front-back ${file ? 'front-back-file-uploaded' : ''} ${isError ? 'error-border' : ''}`}>

            {!file ? (
                <div className="upload-content-front-back upload-content">
                    <span>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <g clip-path="url(#clip0_8155_26658)">
                                <path d="M18 15V18H6V15H4V18C4 19.1 4.9 20 6 20H18C19.1 20 20 19.1 20 18V15H18ZM7 9L8.41 10.41L11 7.83V16H13V7.83L15.59 10.41L17 9L12 4L7 9Z" fill="#00155A" />
                            </g>
                            <defs>
                                <clipPath id="clip0_8155_26658">
                                    <rect width="24" height="24" fill="white" />
                                </clipPath>
                            </defs>
                        </svg>
                    </span>
                    <p>
                        <p style={isError ? {color: "#E7322C", display: "inline"} : {display: "inline"}}>Drop your file here or</p> <span>Browse</span>
                    </p>
                    <small>Supports JPG, PNG document</small>
                    <input
                        type="file"
                        className="upload-auth-input"
                        accept="image/png,image/jpeg"
                        onChange={(e) => handleFileChange(e, type)}
                    />
                </div>
            ) : (
                <div className="upload-content">
                    <img
                      src={typeof file === "string" ? file : URL.createObjectURL(file)}
                      alt={label}
                      style={{ width: "100%", height: "150px", objectFit: "cover" }}
                      onLoad={(e) => {
                        if (typeof file !== "string") {
                          URL.revokeObjectURL((e.target as HTMLImageElement).src);
                        }
                      }}
                    />
                    {isVerified === false && (
                      <span
                          className="custom_delete_icon"
                          onClick={() => removeFile(type)}
                          style={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              cursor: 'pointer',
                          }}
                      >
                          <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                              <path d="M4.16667 1.66667V0H12.5V1.66667H16.6667V3.33333H15V15.8333C15 16.0543 14.9122 16.2663 14.7559 16.4226C14.5996 16.5789 14.3877 16.6667 14.1667 16.6667H2.5C2.27899 16.6667 2.06702 16.5789 1.91074 16.4226C1.75446 16.2663 1.66667 16.0543 1.66667 15.8333V3.33333H0V1.66667H4.16667ZM3.33333 3.33333V15H13.3333V3.33333H3.33333ZM5.83333 5.83333H7.5V12.5H5.83333V5.83333ZM9.16667 5.83333H10.8333V12.5H9.16667V5.83333Z" fill="#E7322C" />
                          </svg>
                      </span>
                    )}
                </div>
            )}
        </div>
        )
      };

    if (isLoading) return <PreloaderWrapper />;
    if (isCheckingLink) return <PreloaderWrapper />
    else if (isLinkExpired) return <ExpiredLink />;

    // if (isSubmitting) return <PreloaderWrapper />

    // if (success) {
    //     return (
    //         <div className="row">
    //             <Alert type="success">Form submitted successfully</Alert>
    //         </div>
    //     )
    // }

    const handleDownloadAuthorization = async (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      try {
        const res = await api2.get(`/api/v2/provider_landing/get_auth_document?id=${auth_document_id}`)(dispatch, false, true);
        const downloadUrl = res.data.download_url;
        setLawFirmName(res.data.name);
        const link = document.createElement("a");
        window.open(downloadUrl, "_blank");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

      } catch (err) {
        console.error("Download failed", err);
      }
    };

    const getObjectUrl = (file: File | string | null): string | null => {
      if (!file) return null;

      if (typeof file === "string") {
        return file; // backend URL
      }

      return URL.createObjectURL(file); // File
    };

    return (
      <>
        {isDocumentUploaded ? (
          <div className='hipaa-wrapper success-page-wrapper'>
            <div className='hipaa-card success-card'>
              <div className="success-iconWrapper">
                <img src={upload_sucess} alt="View" width={140} height={140} />
              </div>

              <h1 className='success-title'>
                HIPAA Authorization Uploaded Successfully!
              </h1>

              <p className='success-subtitle' style={{ marginBottom: '16px' }}>
                We've sent an email to {lawFirmName} notifying them
                that you've submitted your authorization.
              </p>

              <button className="submit-btn">
                <a href={`${window.location.origin}/patient`} className='text-white' target="_blank" rel="noopener noreferrer">Explore ChartRequest</a>
              </button>
            </div>
          </div>
        ) : (
          <div className="hipaa-wrapper">
            <div className="hipaa-card">
                <h3>Upload Signed HIPAA Authorization</h3>
              <p className="subtitle">
                  Please verify that you've completed all sections of your authorization
                  form and added your signature before uploading.
                  You can <a href="#" onClick={handleDownloadAuthorization}>download authorization</a> here.
              </p>

                {/* HIPAA Upload */}
                <div className="upload-section">
                    <div className="upload-header">
                        <img src={image9} alt="" /> Upload Signed HIPAA Authorization
                    </div>

                    <div className="upload-body">
                        <div className={`upload-box ${(submitAttempted || triedAuthUpload) && !authSignFile ? 'error-border' : ''}`}>
                            {!authSignFile ? (
                              <div className="upload-content upload-content-authorization">
                                <span className='svg-icon'>
                                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <g clip-path="url(#clip0_8155_26689)">
                                      <path d="M18 15V18H6V15H4V18C4 19.1 4.9 20 6 20H18C19.1 20 20 19.1 20 18V15H18ZM7 9L8.41 10.41L11 7.83V16H13V7.83L15.59 10.41L17 9L12 4L7 9Z" fill="#00155A" />
                                    </g>
                                    <defs>
                                      <clipPath id="clip0_8155_26689">
                                        <rect width="24" height="24" fill="white" />
                                      </clipPath>
                                    </defs>
                                  </svg>
                                </span>
                                <p>
                                    <p style={(submitAttempted || triedAuthUpload) && !authSignFile ? {color: "#E7322C", display: "inline"} : {display: "inline"}}>Drop your file here or</p> <span>Browse</span>
                                </p>
                                <small>Supports PDF document</small>
                                <input type='file' className='upload-auth-input' accept="application/pdf" onChange={(e) => {handleFileChangeForAuthorization(e);}}/>
                              </div>
                            ) : (
                                <div className={`uploaded_invoice_preview ${providerData?.verification_checklist?.hippa_verified ? 'temp-auth-box' : 'temp-auth-box1'}`}>
                                  <div className="invoice_file_info file-name">
                                    <img src={pdfIcon} alt="PDF" className="pdf_icon" />
                                    <a
                                      href={getObjectUrl(authSignFile)!}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="uploaded_invoice_link file-name"
                                    >
                                      {typeof authSignFile === "string"
                                        ? providerData.signed_documents.signed_authorization_name
                                        : typeof authSignFile !== "string" && authSignFile ? (authSignFile as File).name : ''}
                                    </a>
                                  </div>
                                  <div className="auth_actions">
                                    <img
                                      src={visibility_icon}
                                      alt="View"
                                      width={20}
                                      height={20}
                                      style={{ cursor: "pointer" }}
                                      onClick={() => {
                                        const url = getObjectUrl(authSignFile);
                                        if (!url) return;
                                        window.open(url, "_blank");
                                        if (authSignFile && typeof authSignFile !== "string") {
                                          setTimeout(() => URL.revokeObjectURL(url), 1000);
                                        }
                                      }}
                                    />
                                  {!providerData?.verification_checklist?.hippa_verified == true && (
                                    <button className="delete_button" onClick={removeFileAuthorization}>
                                      <img src={deleteIcon} alt="Delete" />
                                    </button>
                                  )}
                                  </div>
                                </div>
                            )}
                        </div>
                        <div className="blank-error">{(submitAttempted || triedAuthUpload) && !authSignFile && (fileError || 'This is a required field')}</div>
                    </div>
                </div>

                {/* Govt ID Upload */}
                <div className="upload-section">
                    <div className="upload-header">
                        <img src={image10} alt="" />
                        Upload Govt. ID (Front, Back) -{' '}
                        <span className="optional_txt">{government_id}</span>
                    </div>

                    <div className="upload-body grid-2">
                        {renderUploadBox('Gov ID Front', govtIdFiles.front, 'front')}
                        {renderUploadBox('Gov ID Back', govtIdFiles.back, 'back')}
                    </div>
                    {((submitAttempted || govtIdFilesError.front || govtIdFilesError.back) && (!govtIdFiles.front || !govtIdFiles.back) && government_id === 'Required')  && <div className="blank-error-gov">{govtIdFilesError.front ? govtIdFilesError.front : govtIdFilesError.back ? govtIdFilesError.back : 'This is a required field'}</div>}
                </div>

                <button className="submit-btn" onClick={handleSubmit}>
                    Submit
                </button>
            </div>
          </div>
        )}
      
      </>
    )
}